import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { getDisplayName } from '../../../../../../../models/NameUtils';
import { THEME } from '../../../../../../components-v2/Theme/theme';
import { Button } from '../../../../../../components-v2/Button';
import { HandwrittenCardTemplateSearchModal } from '../../../../../../components/HandwrittenCards/HandwrittenCardTemplateSearchModal';
import { CalendarDateIcon } from '../../../../../../components/svgs/icons/CalendarDateIcon';
import { EmailIcon } from '../../../../../../components/svgs/icons/EmailIcon';
import { PhoneIcon } from '../../../../../../components/svgs/icons/PhoneIcon';
import { PostageIcon } from '../../../../../../components/svgs/icons/PostageIcon';
import { TextingIcon } from '../../../../../../components/svgs/icons/TextingIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../../components/Tooltip';
import { PostcardComposerStep } from '../../../../../../containers/PostcardComposer';
import { useScheduleMeetingSettings } from '../../../../../../containers/ScheduleMeeting/useScheduleMeetingSettings';
import { UserSelectionModal } from '../../../../../../components/UserSelectionModal';
import { buildMeetingUrlForContact, getFirstPublishedMeetingConfig } from '../../../../../../utils/meetingUrlBuilder';
import { useMeetingConfigsForUser } from '../../../../../../hooks/useMeetingConfigsForUser';
import { useBookMeetingForContact } from '../../../../../../hooks/useBookMeetingForContact';

import {
	ISingleEmailComposerComponentProps,
	ITextMessagingComponentProps,
	SingleEmailComposerKey,
	TextMessagingViewModelKey,
} from '../../../../../../../models/AppState';
import {
	useEnvironment,
	useFullscreenModal,
	useNoteComposer,
	useUserSession,
} from '../../../../../../../models/hooks/appStateHooks';
import { EventLogger, useEventLogging } from '../../../../../../../models/Logging';
import { styleSheet } from '../styles';
import { toast } from 'sonner';

interface Props extends ISingleEmailComposerComponentProps, ITextMessagingComponentProps {
	contact: Api.IContact;
}

function _QuickActions({ contact, singleEmailComposer, textMessaging }: Props) {
	const userSession = useUserSession();
	const environment = useEnvironment();
	const history = useHistory();
	const noteComposer = useNoteComposer();
	const fullscreenModal = useFullscreenModal();
	const { logApiError } = useEventLogging('QuickActions');

	const { meetingConfigs } = useScheduleMeetingSettings({ user: userSession.user! });
	const { bookMeetingWithMeetingConfigs } = useBookMeetingForContact(contact, meetingConfigs);

	// State for handwritten card template modal
	const [isHwcTemplateModalOpen, setIsHwcTemplateModalOpen] = React.useState(false);
	// State for user selection modal (admin functionality)
	const [showUserSelectionModal, setShowUserSelectionModal] = React.useState(false);
	const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);

	// Fetch meeting configs for selected user when user is selected
	const { data: selectedUserMeetingConfigs } = useMeetingConfigsForUser(selectedUserId);

	// Create a temporary ContactViewModel for legacy functions that still require it
	const tempContactVm = React.useMemo(() => {
		return new Api.ContactViewModel(userSession, contact);
	}, [contact, userSession]);

	const onTextMessageBtnClicked = async () => {
		const phoneNumber = contact.phoneNumbers?.[0];

		if (!phoneNumber) {
			return;
		}

		try {
			if (!textMessaging.phoneNumberOrder) {
				history.push(`/texting`);
				return;
			}

			const conversation = await textMessaging.createConversation([phoneNumber.value]);

			conversation.updateRecipients({
				...contact,
				selectedPhoneNumber: phoneNumber,
			});

			fullscreenModal.dismissModal();

			history.push(`/texting/${textMessaging.id}/${conversation.id}`);
		} catch (err) {
			toast.error('Something went wrong opening text conversation');

			logApiError('CreateConversationFromContact-Error', err);
		}
	};

	const onPhoneCallBtnClicked = () => {
		// Create a phone call note the same way the FAB menu does
		const entities = [tempContactVm];
		const note: Api.INote = Api.createRichContentWithReferencedEntities(entities || []);
		note.context = {
			source: Api.RichContentContextSource.PhoneCall,
		};

		// Add "Phone call with..." to content
		const entityListStringValue = entities.reduce<string>((result, entityModel, i) => {
			const name = (getDisplayName(entityModel) || '').trim();
			if (name) {
				return `${result}${result ? (i === entities.length - 1 ? ' and ' : ', ') : ''}${name}`;
			}
			return result;
		}, '');

		if (entityListStringValue) {
			note.content = Api.createRawRichTextContentStateWithText(`Phone call with ${entityListStringValue}.`);
		}

		EventLogger.logInput('QuickActions', 'AddPhoneCall', 'Click');
		const noteVm = new Api.NoteViewModel(userSession, note);
		noteComposer.show(noteVm);
	};

	const onEmailQuickBtnClicked = () => {
		singleEmailComposer?.showForRecipients([tempContactVm]);
	};

	const onPostageBtnClicked = () => {
		// Open handwritten card template selection modal
		setIsHwcTemplateModalOpen(true);
	};

	const onBookAppointmentBtnClicked = () => {
		const firstPublishedMeeting = getFirstPublishedMeetingConfig(meetingConfigs);

		if (!firstPublishedMeeting) {
			return;
		}

		// Check if current user is an admin
		const isAdmin = userSession.user.groups?.includes('admin') ?? false;

		if (isAdmin) {
			// Show user selection modal for admin users
			setShowUserSelectionModal(true);
		} else {
			// Direct redirect for non-admin users (existing behavior)
			const url = buildMeetingUrlForContact(firstPublishedMeeting, { contactId: contact.id });
			if (url) {
				window.open(url, '_blank');
			}
		}
	};

	const onUserSelectedForMeeting = React.useCallback(
		(selectedUser: Api.IUser) => {
			// If current user is selected, use existing meeting configs directly
			if (selectedUser.id === userSession.user.id) {
				bookMeetingWithMeetingConfigs(meetingConfigs, selectedUser.id);
				setShowUserSelectionModal(false);
			} else {
				// For different user, set selectedUserId to trigger the useMeetingConfigsForUser hook
				setSelectedUserId(selectedUser.id);
			}
		},
		[userSession.user.id, bookMeetingWithMeetingConfigs, meetingConfigs]
	);

	// Effect to handle meeting booking when selected user meeting configs are loaded
	React.useEffect(() => {
		if (selectedUserId && selectedUserMeetingConfigs) {
			bookMeetingWithMeetingConfigs(selectedUserMeetingConfigs, selectedUserId);
			// Reset selection after booking
			setSelectedUserId(null);
			setShowUserSelectionModal(false);
		}
	}, [selectedUserId, selectedUserMeetingConfigs, bookMeetingWithMeetingConfigs]);

	// Check if features are enabled and environment is web
	const canShowEmail = environment?.appType === 'web';
	const canShowPhone = environment?.appType === 'web' && contact.phoneNumbers?.length > 0;
	const canShowText =
		environment?.appType === 'web' &&
		contact.phoneNumbers &&
		Api.VmUtils.getTextMessageCapablePhoneNumbers(contact.phoneNumbers).length > 0;
	const canShowHandwrittenCards =
		environment?.appType === 'web' &&
		userSession.account.features.handwrittenCards?.enabled &&
		userSession.user.userGroupPermissions?.featurePermissions?.handwrittenCard?.send;
	const canShowAppointment =
		environment?.appType === 'web' &&
		userSession?.user?.emailProviderFeatures?.calendar?.canAddUpdateEvents &&
		userSession?.account?.features?.meetingScheduler?.enabled &&
		meetingConfigs?.some(config => config.isPublished);

	return (
		<>
			{canShowText && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button size='icon' onClick={onTextMessageBtnClicked} styles={[styleSheet.quickActionButton]}>
							<TextingIcon
								fill={THEME.colors.primary.contrastText}
								width={20}
								height={20}
								className={css(styleSheet.quickActionIcon)}
							/>
						</Button>
					</TooltipTrigger>
					<TooltipContent>Send a Text Message</TooltipContent>
				</Tooltip>
			)}

			{canShowPhone && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button size='icon' onClick={onPhoneCallBtnClicked} styles={[styleSheet.quickActionButton]}>
							<PhoneIcon
								fill={THEME.colors.primary.contrastText}
								width={24}
								height={24}
								className={css(styleSheet.quickActionIcon)}
							/>
						</Button>
					</TooltipTrigger>
					<TooltipContent>Make a Phone Call</TooltipContent>
				</Tooltip>
			)}

			{canShowEmail && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button size='icon' onClick={onEmailQuickBtnClicked} styles={[styleSheet.quickActionButton]}>
							<EmailIcon
								fillColor={THEME.colors.primary.contrastText}
								width={24}
								height={24}
								className={css(styleSheet.quickActionIcon)}
							/>
						</Button>
					</TooltipTrigger>
					<TooltipContent>Send Email</TooltipContent>
				</Tooltip>
			)}

			{canShowHandwrittenCards && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button size='icon' onClick={onPostageBtnClicked} styles={[styleSheet.quickActionButton]}>
							<PostageIcon
								fillColor={THEME.colors.primary.contrastText}
								width={24}
								height={24}
								className={css(styleSheet.quickActionIcon)}
							/>
						</Button>
					</TooltipTrigger>
					<TooltipContent>Send handwritten card</TooltipContent>
				</Tooltip>
			)}

			{canShowAppointment && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button size='icon' onClick={onBookAppointmentBtnClicked} styles={[styleSheet.quickActionButton]}>
							<CalendarDateIcon
								fillColor={THEME.colors.primary.contrastText}
								width={16}
								height={14}
								className={css(styleSheet.quickActionIcon)}
							/>
						</Button>
					</TooltipTrigger>
					<TooltipContent>Book appointment</TooltipContent>
				</Tooltip>
			)}

			{isHwcTemplateModalOpen && (
				<HandwrittenCardTemplateSearchModal
					isOpen={isHwcTemplateModalOpen}
					onClose={() => setIsHwcTemplateModalOpen(false)}
					onTemplateClicked={(template: Api.IHandwrittenCardTemplate) => {
						setIsHwcTemplateModalOpen(false);
						fullscreenModal.history.push({
							pathname: `/postcard/${template.id}/${PostcardComposerStep.Preview}`,
							state: {
								contactId: contact.id,
							},
						});
					}}
				/>
			)}

			{showUserSelectionModal && (
				<UserSelectionModal
					isOpen={showUserSelectionModal}
					onClose={() => setShowUserSelectionModal(false)}
					onUserSelected={onUserSelectedForMeeting}
					currentUser={userSession.user}
					userSession={userSession}
					title='Select Calendar for Appointment'
					description={`Choose which calendar to book the appointment with ${getDisplayName(contact)}.`}
					initialSelectedUser={userSession.user}
				/>
			)}
		</>
	);
}

const QuickActionsWithObserver = observer(_QuickActions);

export const QuickActions = inject(SingleEmailComposerKey, TextMessagingViewModelKey)(QuickActionsWithObserver);
